import React from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type {
  WorkoutRecord,
  WorkoutSupersetRecord,
  WorkoutExerciseRecord,
} from '~/powersync/generated/schema';
import { ChatExerciseRow } from './ChatExerciseRow';
import { ChatSupersetGroup } from './ChatSupersetGroup';
import { PresentationCaption } from './PresentationCaption';

/**
 * One superset within the workout. A single-exercise superset renders as a
 * standalone exercise row card; a multi-exercise superset renders as a grouped
 * "Superset" card. Its own hook keeps the parent loop hook-safe.
 */
const WorkoutSupersetSection: React.FC<{ superset: WorkoutSupersetRecord }> = ({
  superset,
}) => {
  const { data: exercises } = useLivePowerSyncQuery<WorkoutExerciseRecord>(
    db.Workout.queries.findExercisesBySuperset(superset.id),
    [superset.id],
  );

  if (!exercises || exercises.length === 0) {
    return null;
  }

  if (exercises.length === 1) {
    return (
      <View className="bg-surface rounded-lg p-4">
        <ChatExerciseRow
          exerciseId={exercises[0].id}
          collapsible
          defaultExpanded={false}
          className="py-0"
        />
      </View>
    );
  }

  return <ChatSupersetGroup superset={superset} />;
};

interface WorkoutCardProps {
  caption?: string | null;
  workout: WorkoutRecord;
}

interface WorkoutCardSyncedProps {
  payload: Record<string, unknown>;
}

interface WorkoutPayload {
  caption?: string | null;
  workoutId?: string;
}

/**
 * `present_workout` (design QV39F) → workout header (name + meta) followed by
 * each superset/exercise as an interactive, collapsible card.
 */
export const WorkoutCard: React.FC<WorkoutCardProps> & {
  Synced: React.FC<WorkoutCardSyncedProps>;
} = ({ caption, workout }) => {
  const { t } = useTranslation('workout');

  const { data: supersets } = useLivePowerSyncQuery<WorkoutSupersetRecord>(
    db.Workout.queries.findSupersetsWithExercises(workout.id),
    [workout.id],
  );

  const { data: exercises } = useLivePowerSyncQuery<WorkoutExerciseRecord>(
    db.Workout.queries.findExercisesByWorkout(workout.id),
    [workout.id],
  );

  const exerciseCount = exercises?.length ?? 0;
  const meta = [
    exerciseCount > 0 ? t('exercises', { count: exerciseCount }) : null,
    (workout.duration ?? 0) > 0
      ? t('duration', { duration: workout.duration })
      : null,
  ].filter(Boolean);

  const showCaption =
    !!caption &&
    caption.trim().toLowerCase() !== workout.name.trim().toLowerCase();

  return (
    <View className="w-full py-2 gap-3">
      {showCaption && <PresentationCaption caption={caption} />}
      <View className="gap-0.5 px-1">
        <Text className="text-lg font-sora-semibold text-text-primary">
          {workout.name}
        </Text>
        {meta.length > 0 && (
          <Text className="text-sm text-text-secondary">
            {meta.join(' · ')}
          </Text>
        )}
      </View>
      <View className="gap-3">
        {(supersets ?? []).map((superset) => (
          <WorkoutSupersetSection key={superset.id} superset={superset} />
        ))}
      </View>
    </View>
  );
};

const WorkoutCardSynced: React.FC<WorkoutCardSyncedProps> = ({ payload }) => {
  const { t } = useTranslation('workout');
  const { caption, workoutId } = payload as WorkoutPayload;

  const { data: workout, isLoading } = useLivePowerSyncQuery<WorkoutRecord>(
    workoutId ? db.Workout.queries.findById(workoutId) : null,
    [workoutId],
    { single: true },
  );

  if (!workoutId) {
    return null;
  }

  if (isLoading) {
    return (
      <View className="w-full py-2 gap-2">
        <PresentationCaption caption={caption} />
        <View className="bg-surface rounded-2xl p-4">
          <Text className="text-text-muted text-sm">
            {t('progress.loading')}
          </Text>
        </View>
      </View>
    );
  }

  if (!workout) {
    return null;
  }

  return <WorkoutCard caption={caption} workout={workout} />;
};

WorkoutCard.Synced = WorkoutCardSynced;
